import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Settings,
  Phone,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  MapPin,
  DollarSign,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hotlineNumber, setHotlineNumber] = useState('1800 000 000');
  const [routingConfig, setRoutingConfig] = useState({
    routing_max_radius_km: '100',
    ring_timeout_seconds: '20',
    max_attempts: '8',
    fallback_mode: 'voicemail',
    use_travel_time_api: 'false'
  });
  const [stripeConfig, setStripeConfig] = useState({
    stripe_mode: 'test',
    credentials_configured: false
  });
  const [clearingData, setClearingData] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchConfigs();
    }
  }, [user]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      
      // Fetch all configs
      const allConfigsResponse = await axios.get(`${API}/admin/config`);
      const configsMap = {};
      allConfigsResponse.data.forEach(config => {
        configsMap[config.key] = config.value;
      });
      setConfigs(configsMap);
      
      // Set individual config values with defaults
      setHotlineNumber(configsMap.support_hotline_number || '1800 000 000');
      setRoutingConfig({
        routing_max_radius_km: configsMap.routing_max_radius_km || '100',
        ring_timeout_seconds: configsMap.ring_timeout_seconds || '20',
        max_attempts: configsMap.max_attempts || '8',
        fallback_mode: configsMap.fallback_mode || 'voicemail',
        use_travel_time_api: configsMap.use_travel_time_api || 'false'
      });
      
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHotline = async () => {
    if (!hotlineNumber.trim()) {
      toast.error('Hotline number cannot be empty');
      return;
    }

    try {
      setSaving(true);
      
      await axios.put(`${API}/admin/config/support_hotline_number`, {
        value: hotlineNumber.trim()
      });
      
      toast.success('Emergency hotline number updated successfully');
      fetchConfigs(); // Refresh configs
      
    } catch (error) {
      console.error('Failed to update hotline number:', error);
      toast.error('Failed to update hotline number');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRoutingConfig = async () => {
    try {
      setSaving(true);
      
      // Save all routing configuration values
      const configPromises = Object.entries(routingConfig).map(([key, value]) =>
        axios.put(`${API}/admin/config/${key}`, { value: value.toString() })
      );
      
      await Promise.all(configPromises);
      
      toast.success('Hotline routing configuration updated successfully');
      fetchConfigs(); // Refresh configs
      
    } catch (error) {
      console.error('Failed to update routing config:', error);
      toast.error('Failed to update routing configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAllJobs = async () => {
    if (!window.confirm('Are you sure you want to clear ALL jobs and related data? This action cannot be undone.')) {
      return;
    }
    
    if (!window.confirm('This will permanently delete all jobs, timelines, payments, and notifications. Type CONFIRM to proceed.')) {
      return;
    }

    try {
      setClearingData(true);
      
      const response = await axios.delete(`${API}/admin/jobs/clear-all`);
      
      toast.success(`Database cleared successfully! Deleted ${response.data.deleted_jobs} jobs and ${response.data.deleted_related_records} related records.`);
      
    } catch (error) {
      console.error('Failed to clear database:', error);
      toast.error('Failed to clear database');
    } finally {
      setClearingData(false);
    }
  };

  const handleRoutingChange = (key, value) => {
    setRoutingConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatHotlineForDisplay = (number) => {
    // Format for better readability: 1800 000 000 or 13 11 66
    return number.replace(/(\d{2,4})(\d{3})(\d{3})/g, '$1 $2 $3').trim();
  };

  const validateHotlineNumber = (number) => {
    // Basic validation for Australian phone numbers
    const cleaned = number.replace(/\s/g, '');
    const isValid = /^(1800|1300|13\d{4}|0[2-9]\d{8})$/.test(cleaned);
    return isValid;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/admin')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <Settings className="h-6 w-6 mr-2" />
                  Admin Settings
                </h1>
                <p className="text-sm text-gray-600">Configure system settings and preferences</p>
              </div>
            </div>
            
            <button
              onClick={fetchConfigs}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stripe Connect Express Configuration */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-purple-100 rounded-lg mr-4">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Stripe Connect Express Integration</h2>
              <p className="text-sm text-gray-600">Configure live payment processing with Stripe Connect</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-900 mb-1">Ready for Live Mode</h3>
                <p className="text-sm text-blue-700">
                  Your escrow payment system is ready for production. Add your Stripe credentials below to enable live payment processing.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Mode
                </label>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-2 rounded-lg font-medium ${
                    stripeConfig.credentials_configured 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {stripeConfig.credentials_configured ? 'LIVE MODE READY' : 'TEST MODE'}
                  </span>
                  {stripeConfig.credentials_configured && (
                    <span className="text-xs text-green-600">✓ Credentials configured</span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="text-sm text-gray-600">
                  {stripeConfig.credentials_configured 
                    ? 'Ready for live payment processing'
                    : 'Add credentials to enable live mode'
                  }
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800 mb-2">Stripe Credentials Required</h3>
                  <div className="text-sm text-amber-700 space-y-2">
                    <p>To enable live payment processing, you need to configure your Stripe credentials in the backend environment:</p>
                    <div className="bg-amber-100 rounded p-3 font-mono text-xs space-y-1">
                      <div>STRIPE_SECRET_KEY=sk_live_...</div>
                      <div>STRIPE_PUBLISHABLE_KEY=pk_live_...</div>
                      <div>STRIPE_WEBHOOK_SECRET=whsec_...</div>
                    </div>
                    <p><strong>Security Note:</strong> Credentials are stored securely in backend environment variables and never exposed client-side.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900 mb-2">Features Ready for Live Mode:</h3>
              <div className="text-sm text-green-700 space-y-1">
                <div>✓ Escrow payment flow with commission calculation</div>
                <div>✓ Webhook signature verification and idempotency</div>
                <div>✓ Payments ledger for reconciliation</div>
                <div>✓ Automatic refunds for cancellations</div>
                <div>✓ Complete audit trail and timeline logging</div>
                <div>✓ Admin bulk operations and overrides</div>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Hotline Configuration */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-red-100 rounded-lg mr-4">
              <Phone className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Emergency Breakdown Hotline</h2>
              <p className="text-sm text-gray-600">Configure the emergency hotline number for breakdown assistance</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-amber-800 mb-1">Important</h3>
                <p className="text-sm text-amber-700">
                  This number will be displayed to all users for emergency breakdowns. Ensure it's a valid, 
                  monitored 24/7 hotline that can handle emergency service requests.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Hotline Number
              </label>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={hotlineNumber}
                    onChange={(e) => setHotlineNumber(e.target.value)}
                    className="jop-input"
                    placeholder="1800 000 000"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Format: 1800 XXX XXX, 1300 XXX XXX, or 13 XX XX
                  </div>
                </div>
                <button
                  onClick={handleSaveHotline}
                  disabled={saving || !validateHotlineNumber(hotlineNumber)}
                  className="jop-button flex items-center"
                >
                  {saving ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
              
              {!validateHotlineNumber(hotlineNumber) && hotlineNumber.trim() && (
                <div className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Please enter a valid Australian phone number
                </div>
              )}
            </div>

            {/* Current Display Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Preview</h3>
              <p className="text-sm text-gray-600 mb-3">How this will appear to users:</p>
              <div className="bg-red-600 text-white rounded-lg p-3 flex items-center">
                <Phone className="h-5 w-5 mr-3" />
                <div>
                  <div className="font-medium">Emergency Breakdown</div>
                  <div className="text-sm text-red-100">24/7 Hotline: {formatHotlineForDisplay(hotlineNumber)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hotline Routing Configuration */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <MapPin className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Emergency Hotline Routing</h2>
              <p className="text-sm text-gray-600">Configure how emergency calls are routed to on-call dealers</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Routing Radius (km)
              </label>
              <input
                type="number"
                min="10"
                max="500"
                value={routingConfig.routing_max_radius_km}
                onChange={(e) => handleRoutingChange('routing_max_radius_km', e.target.value)}
                className="jop-input"
                placeholder="100"
              />
              <div className="mt-1 text-xs text-gray-500">
                Maximum distance to search for available dealers
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ring Timeout (seconds)
              </label>
              <input
                type="number"
                min="10"
                max="60"
                value={routingConfig.ring_timeout_seconds}
                onChange={(e) => handleRoutingChange('ring_timeout_seconds', e.target.value)}
                className="jop-input"
                placeholder="20"
              />
              <div className="mt-1 text-xs text-gray-500">
                How long to ring each dealer before moving to next
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Attempts
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={routingConfig.max_attempts}
                onChange={(e) => handleRoutingChange('max_attempts', e.target.value)}
                className="jop-input"
                placeholder="8"
              />
              <div className="mt-1 text-xs text-gray-500">
                Maximum number of dealers to attempt before fallback
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fallback Mode
              </label>
              <select
                value={routingConfig.fallback_mode}
                onChange={(e) => handleRoutingChange('fallback_mode', e.target.value)}
                className="jop-select"
              >
                <option value="voicemail">Voicemail</option>
                <option value="escalate_to_admin">Escalate to Admin</option>
              </select>
              <div className="mt-1 text-xs text-gray-500">
                What to do when no dealers accept the call
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={routingConfig.use_travel_time_api === 'true'}
                onChange={(e) => handleRoutingChange('use_travel_time_api', e.target.checked ? 'true' : 'false')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Use Google Distance Matrix API for travel time routing
              </span>
            </label>
            <div className="mt-1 text-xs text-gray-500 ml-6">
              When enabled, dealers are sorted by estimated travel time instead of straight-line distance
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveRoutingConfig}
              disabled={saving}
              className="jop-button flex items-center"
            >
              {saving ? (
                <div className="spinner" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Routing Settings
                </>
              )}
            </button>
          </div>
        </div>

        {/* Hotline Analytics */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <RefreshCw className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Hotline Analytics</h2>
              <p className="text-sm text-gray-600">Monitor emergency hotline performance and usage</p>
            </div>
          </div>
          
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>Hotline analytics will be displayed here once call routing is active.</p>
          </div>
        </div>

        {/* Database Cleanup */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-red-100 rounded-lg mr-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Database Cleanup</h2>
              <p className="text-sm text-gray-600">Clear all test data for fresh testing environment</p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900 mb-2">⚠️ DESTRUCTIVE ACTION</h3>
                <div className="text-sm text-red-700 space-y-1">
                  <p>This will permanently delete ALL jobs and related data including:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>All job postings and details</li>
                    <li>Job timelines and audit logs</li>
                    <li>Payment records and escrow transactions</li>
                    <li>Commission calculations and payouts</li>
                    <li>Emergency call logs and hotline sessions</li>
                    <li>Bulk action history and notifications</li>
                  </ul>
                  <p className="font-medium">This action cannot be undone!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Clear All Test Data</h3>
              <p className="text-sm text-gray-600">Remove all jobs to start with fresh testing environment</p>
            </div>
            
            <button
              onClick={handleClearAllJobs}
              disabled={clearingData}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {clearingData ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {clearingData ? 'Clearing...' : 'Clear All Jobs'}
            </button>
          </div>
        </div>

        {/* Future Settings */}
        <div className="jop-card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Other Settings</h2>
          <div className="text-center py-8 text-gray-500">
            <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>Additional system settings will be added here in future updates.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;